import { useId } from "react";
import { kickAudio, playNow } from "@/audio/piano";
import { useKey } from "@/theory/key";

/**
 * Piano diagram. SVG-rendered, dark-PI palette by default.
 * Highlights are described by pitch class (0=C, 1=C#, ..., 11=B) so callers
 * never deal with octave-suffixed pitch names. Optional `keys` prop lights
 * specific MIDI numbers when register matters (e.g. left-hand bass register).
 *
 * No "C4 / D#3" text ever appears in the rendered UI. If you want a label on
 * a key, pass it via `label` — the lesson decides what to write
 * ("ROOT", "I", "BASS", "1", etc.).
 */

export type Tone =
  | "root"
  | "voice"
  | "bass"
  | "melody"
  | "highlight";

export interface KeyHighlight {
  /** Pitch class 0–11 (C=0). Lights up matching key in every octave shown. */
  pc?: number;
  /** Specific MIDI number — overrides pc. Lights only that one key. */
  midi?: number;
  /**
   * Scale degree (1=tonic, 2=supertonic, …, 7=leading tone).
   * Resolved against the current <KeyProvider>'s tonic + quality.
   * Use this when you want the highlight to follow transposition.
   */
  degree?: number;
  /** Visual treatment. Default "highlight". */
  tone?: Tone;
  /** Optional label rendered on the key. Keep short (1–4 chars). */
  label?: string;
}

interface PianoDiagramProps {
  highlights?: KeyHighlight[];
  /** Number of octaves to render. Default 2. */
  octaves?: number;
  /** Starting octave (MIDI octave number). Default 3 — middle register. */
  startOctave?: number;
  /** Show one extra C at the right edge so the highest octave looks complete. */
  trailingC?: boolean;
  /** When true, clicking a key plays it via the Salamander piano. */
  playable?: boolean;
  /** Compact size (good for inline use). Default false (full size). */
  compact?: boolean;
  /** Optional aria-label / sr description. */
  ariaLabel?: string;
}

const WHITE_PCS = [0, 2, 4, 5, 7, 9, 11];
const BLACK_PCS = [1, 3, 6, 8, 10];
// Where each black key sits, expressed as the index of the white key it follows
// (within an octave starting on C). Order: C# Db = after C(0), D# Eb = after D(1), etc.
const BLACK_OFFSETS: Record<number, number> = { 1: 1, 3: 2, 6: 4, 8: 5, 10: 6 };

const TONE_FILL: Record<Tone, { white: string; black: string; text: string; ring: string }> = {
  root:      { white: "url(#piRootW)",  black: "url(#piRootB)",  text: "#1F1B14", ring: "#F59E0B" },
  voice:     { white: "url(#piVoiceW)", black: "url(#piVoiceB)", text: "#E0F2FE", ring: "#22D3EE" },
  bass:      { white: "url(#piBassW)",  black: "url(#piBassB)",  text: "#FFF7ED", ring: "#FB7185" },
  melody:    { white: "url(#piMelodyW)",black: "url(#piMelodyB)",text: "#022C22", ring: "#34D399" },
  highlight: { white: "url(#piVoiceW)", black: "url(#piVoiceB)", text: "#E0F2FE", ring: "#22D3EE" },
};

// Convert MIDI to a pitch name for the audio engine ONLY (never displayed).
function midiToPitchName(midi: number): string {
  const pc = ((midi % 12) + 12) % 12;
  const oct = Math.floor(midi / 12) - 1;
  const sharps = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  return `${sharps[pc]}${oct}`;
}

export function PianoDiagram({
  highlights = [],
  octaves = 2,
  startOctave = 3,
  trailingC = true,
  playable = false,
  compact = false,
  ariaLabel = "Piano keyboard",
}: PianoDiagramProps) {
  const uid = useId().replace(/[:]/g, "");
  const k = useKey();

  // Resolve scale-degree highlights against the current key, if any
  const resolved: KeyHighlight[] = highlights.map((h) => {
    if (h.degree != null && k) {
      return { ...h, pc: k.pcFromDegree(h.degree) };
    }
    return h;
  });

  // Build the set of white and black keys we'll render
  type Rendered = { midi: number; pc: number; xIdx: number };
  const whiteKeys: Rendered[] = [];
  const blackKeys: Rendered[] = [];
  let whiteCounter = 0;
  for (let o = 0; o <= octaves; o++) {
    WHITE_PCS.forEach((pc, i) => {
      // Render the trailing C of the last octave only when trailingC is on
      if (o === octaves && i > 0) return;
      if (o === octaves && !trailingC) return;
      const midi = (startOctave + o + 1) * 12 + pc; // MIDI 24 = C1, 36 = C2, etc.
      whiteKeys.push({ midi, pc, xIdx: whiteCounter });
      whiteCounter += 1;
    });
    if (o < octaves) {
      BLACK_PCS.forEach((pc) => {
        const midi = (startOctave + o + 1) * 12 + pc;
        blackKeys.push({ midi, pc, xIdx: o * 7 });
      });
    }
  }

  // Build a lookup from MIDI → highlight (specific midi takes precedence over pc-wide)
  const byMidi = new Map<number, KeyHighlight>();
  const byPc = new Map<number, KeyHighlight>();
  for (const h of resolved) {
    if (h.midi != null) byMidi.set(h.midi, h);
    else if (h.pc != null) byPc.set(((h.pc % 12) + 12) % 12, h);
  }
  const findHighlight = (midi: number, pc: number): KeyHighlight | undefined => {
    return byMidi.get(midi) ?? byPc.get(pc);
  };

  const kw = compact ? 38 : 50;
  const kh = compact ? 158 : 200;
  const bkw = compact ? 24 : 32;
  const bkh = compact ? 100 : 130;
  const labelSize = compact ? 11 : 13;

  const totalW = whiteCounter * kw;

  const handleClick = (midi: number) => {
    if (!playable) return;
    kickAudio();
    void playNow(midiToPitchName(midi), "8n", 0.85);
  };

  return (
    <div className="my-4">
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`-2 -2 ${totalW + 4} ${kh + 4}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: "100%", height: "auto", display: "block" }}
      >
        <defs>
          {/* WHITE KEYS — neutral cream */}
          <linearGradient id={`piWhite${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F8FAFC" />
            <stop offset="100%" stopColor="#CBD5E1" />
          </linearGradient>
          {/* BLACK KEYS — deep slate */}
          <linearGradient id={`piBlack${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0F172A" />
            <stop offset="100%" stopColor="#020617" />
          </linearGradient>

          {/* HIGHLIGHT VARIANTS */}
          <linearGradient id="piRootW" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FCD34D" />
            <stop offset="100%" stopColor="#F59E0B" />
          </linearGradient>
          <linearGradient id="piRootB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D97706" />
            <stop offset="100%" stopColor="#92400E" />
          </linearGradient>
          <linearGradient id="piVoiceW" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#67E8F9" />
            <stop offset="100%" stopColor="#0891B2" />
          </linearGradient>
          <linearGradient id="piVoiceB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0E7490" />
            <stop offset="100%" stopColor="#083344" />
          </linearGradient>
          <linearGradient id="piBassW" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FB7185" />
            <stop offset="100%" stopColor="#BE123C" />
          </linearGradient>
          <linearGradient id="piBassB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#9F1239" />
            <stop offset="100%" stopColor="#4C0519" />
          </linearGradient>
          <linearGradient id="piMelodyW" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#86EFAC" />
            <stop offset="100%" stopColor="#16A34A" />
          </linearGradient>
          <linearGradient id="piMelodyB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#15803D" />
            <stop offset="100%" stopColor="#052E16" />
          </linearGradient>

          <filter id={`piGlow${uid}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* WHITE KEYS first */}
        {whiteKeys.map((k) => {
          const h = findHighlight(k.midi, k.pc);
          const tone = h?.tone ?? "highlight";
          const fill = h ? TONE_FILL[tone].white : `url(#piWhite${uid})`;
          const ring = h ? TONE_FILL[tone].ring : "#475569";
          const tx = h ? TONE_FILL[tone].text : "#0F172A";
          return (
            <g
              key={`w${k.midi}`}
              onClick={() => handleClick(k.midi)}
              style={{ cursor: playable ? "pointer" : "default" }}
            >
              <rect
                x={k.xIdx * kw}
                y={0}
                width={kw - 1.5}
                height={kh}
                rx={3}
                fill={fill}
                stroke={ring}
                strokeWidth={h ? 1.6 : 1}
                filter={h ? `url(#piGlow${uid})` : undefined}
                style={{ transition: "fill 220ms ease-out, stroke 220ms ease-out" }}
              />
              {h?.label && (
                <text
                  x={k.xIdx * kw + (kw - 1.5) / 2}
                  y={kh - 14}
                  textAnchor="middle"
                  style={{
                    fontFamily: '"Inter", system-ui, sans-serif',
                    fontSize: labelSize,
                    fontWeight: 700,
                    fill: tx,
                    letterSpacing: "0.02em",
                    pointerEvents: "none",
                  }}
                >
                  {h.label}
                </text>
              )}
            </g>
          );
        })}

        {/* BLACK KEYS on top */}
        {blackKeys.map((k) => {
          const h = findHighlight(k.midi, k.pc);
          const tone = h?.tone ?? "highlight";
          const fill = h ? TONE_FILL[tone].black : `url(#piBlack${uid})`;
          const ring = h ? TONE_FILL[tone].ring : "#020617";
          const tx = h ? TONE_FILL[tone].text : "#F8FAFC";
          const xPos = (k.xIdx + BLACK_OFFSETS[k.pc]) * kw - bkw / 2;
          return (
            <g
              key={`b${k.midi}`}
              onClick={() => handleClick(k.midi)}
              style={{ cursor: playable ? "pointer" : "default" }}
            >
              <rect
                x={xPos}
                y={0}
                width={bkw}
                height={bkh}
                rx={2}
                fill={fill}
                stroke={ring}
                strokeWidth={h ? 1.6 : 1}
                filter={h ? `url(#piGlow${uid})` : undefined}
                style={{ transition: "fill 220ms ease-out, stroke 220ms ease-out" }}
              />
              {h?.label && (
                <text
                  x={xPos + bkw / 2}
                  y={bkh - 10}
                  textAnchor="middle"
                  style={{
                    fontFamily: '"Inter", system-ui, sans-serif',
                    fontSize: labelSize - 2,
                    fontWeight: 700,
                    fill: tx,
                    letterSpacing: "0.02em",
                    pointerEvents: "none",
                  }}
                >
                  {h.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Convenience: convert a letter (with optional accidental) to a pitch class. */
export function pc(letter: string): number {
  const map: Record<string, number> = {
    C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5,
    "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
  };
  const v = map[letter];
  if (v == null) throw new Error(`Unknown pitch letter: ${letter}`);
  return v;
}
